import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/axios'
import { API_ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/stores/useAuthStore'

export interface ProjectListItem {
    id: string
    name: string
    document_type: number | null
    document_type_name: string
    width_mm: number
    height_mm: number
    thumbnail_url: string
    is_template: boolean
    status: string
    page_count: number
    created: string
    updated: string
}

export interface ProjectDetail extends ProjectListItem {
    pages: {
        id: number
        page_number: number
        canvas_json: Record<string, unknown>
        thumbnail_url: string
        created: string
        updated: string
    }[]
}

interface CreateProjectPayload {
    name: string
    document_type?: number | null
    width_mm: number
    height_mm: number
}

interface PaginatedResponse<T> {
    count: number
    next: string | null
    previous: string | null
    results: T[]
}

const KEYS = {
    all: ['projects'] as const,
    list: () => [...KEYS.all, 'list'] as const,
    detail: (id: string) => [...KEYS.all, 'detail', id] as const,
}

export function useProjects() {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: KEYS.list(),
        queryFn: async () => {
            const { data } = await api.get<PaginatedResponse<ProjectListItem>>(API_ROUTES.projects)
            return data.results
        },
        enabled: isLogged,
    })
}

export function useProject(id: string) {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: KEYS.detail(id),
        queryFn: async () => {
            const { data } = await api.get<ProjectDetail>(`${API_ROUTES.projects}${id}/`)
            return data
        },
        enabled: isLogged && !!id,
    })
}

export function useCreateProject() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: CreateProjectPayload) => {
            const { data } = await api.post<ProjectDetail>(API_ROUTES.projects, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
        },
    })
}

export function useDeleteProject() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`${API_ROUTES.projects}${id}/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
        },
    })
}
